
import { useState, useEffect } from 'react';
import { mapboxgl } from '../utils/mapboxConfig';
import { useToast } from '@/hooks/use-toast';

interface UseMapInitializationProps {
  mapContainer: React.RefObject<HTMLDivElement>;
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  defaultCenter: [number, number];
  defaultZoom: number;
  onInteraction: () => void;
}

export const useMapInitialization = ({
  mapContainer,
  mapRef,
  defaultCenter,
  defaultZoom,
  onInteraction
}: UseMapInitializationProps) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    if (!mapContainer.current) return;
    
    if (!mapboxgl.accessToken) {
      console.error('Mapbox token is missing');
      setMapError('Mapbox access token is missing. Please check your configuration.');
      return;
    }
    
    // Prevent double initialization
    if (mapRef.current) {
      return;
    }

    try {
      // Use default center for initial load
      mapRef.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: defaultCenter,
        zoom: defaultZoom,
      });
      
      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      mapRef.current.addControl(new mapboxgl.FullscreenControl());
      
      // Set up interaction handling
      mapRef.current.on('dragstart', onInteraction);
      mapRef.current.on('zoomstart', onInteraction);
      
      mapRef.current.on('idle', () => {
        setMapLoaded(true);
      });
      
      mapRef.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setMapError('Error loading map. Please check if Mapbox is accessible.');
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Failed to initialize map. Please try reloading the page.');
    }
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // Only run once on mount
  
  return { mapLoaded, mapError };
};
